import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  BookOpen,
  FolderOpen,
  Users,
  Calendar,
  CalendarDays,
  Settings,
  LogOut,
  ArrowLeft,
  Menu,
  X,
  ClipboardList,
  User
} from 'lucide-react';
import { getUsers } from '../firebase/userService';
import { isAdmin, canAccessLessonPlanner, canAccessTaskManager, canAccessDailySummary } from '../utils/permissions';
import './DashboardLayout.css';

function DashboardLayout({ children }) {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser?.email) {
        const usersResult = await getUsers();
        if (usersResult.success) {
          const userData = usersResult.users.find(u => u.email?.toLowerCase() === currentUser.email.toLowerCase());
          setCurrentUserData(userData);
        }
      }
      setLoading(false);
    };

    loadUserData();
  }, [currentUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile Menu Button */}
      <button className="mobile-menu-button" onClick={() => setSidebarOpen(true)}>
        <Menu size={24} />
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button className="close-sidebar-button" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
          <Link to="/" className="back-home">
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </Link>
          <h1 className="sidebar-title">Mr. Willeby</h1>
        </div>

        <nav className="sidebar-nav">
          {/* Lesson Planner sections - Admin only */}
          {canAccessLessonPlanner(currentUserData) && (
            <>
              <Link
                to="/planner"
                className={`nav-link ${isActive('/planner') && location.pathname === '/planner' ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Home size={20} />
                <span>Dashboard</span>
              </Link>

              <Link
                to="/planner/classes"
                className={`nav-link ${isActive('/planner/classes') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Users size={20} />
                <span>Classes</span>
              </Link>

              <Link
                to="/planner/units"
                className={`nav-link ${isActive('/planner/units') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FolderOpen size={20} />
                <span>Units</span>
              </Link>

              <Link
                to="/planner/lessons"
                className={`nav-link ${isActive('/planner/lessons') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <BookOpen size={20} />
                <span>Lessons</span>
              </Link>

              <Link
                to="/planner/assignments"
                className={`nav-link ${isActive('/planner/assignments') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <ClipboardList size={20} />
                <span>Assignments</span>
              </Link>

              <Link
                to="/planner/calendar"
                className={`nav-link ${isActive('/planner/calendar') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Calendar size={20} />
                <span>Calendar</span>
              </Link>

              <Link
                to="/planner/schedule"
                className={`nav-link ${isActive('/planner/schedule') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <CalendarDays size={20} />
                <span>Weekly Schedule</span>
              </Link>
            </>
          )}

          {/* Daily Summary - Admin only */}
          {canAccessDailySummary(currentUserData) && (
            <Link
              to="/daily-summary-manager"
              className={`nav-link ${isActive('/daily-summary-manager') ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <CalendarDays size={20} />
              <span>Daily Schedule Manager</span>
            </Link>
          )}

          {/* Integrations - Admin only */}
          {canAccessLessonPlanner(currentUserData) && (
            <Link
              to="/planner/integrations"
              className={`nav-link ${isActive('/planner/integrations') ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Settings size={20} />
              <span>Integrations</span>
            </Link>
          )}
        </nav>

        <div className="sidebar-footer">
          <Link
            to="/profile"
            className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <User size={20} />
            <span>Profile</span>
          </Link>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="dashboard-main">
        {children}
      </main>
    </div>
  );
}

export default DashboardLayout;
